import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { FiArrowRight, FiCheckCircle, FiLock, FiStar, FiShield, FiHelpCircle } from 'react-icons/fi';
import { FaWhatsapp } from 'react-icons/fa';
import Hero from './Hero';
import ProductCard from './ProductCard';
import ProductModal from './ProductModal';
import SafeIcon from '../common/SafeIcon';
import { shopProducts } from '../data/shopData';
import Features from './Features';
import Specialties from './Specialties';
import SocialLearning from './SocialLearning';
import ProblemSolution from './ProblemSolution';
import Biography from './Biography';
import QuickFacts from './QuickFacts';
import MediaLogos from './MediaLogos';
import PricingComparison from './PricingComparison';
import CatalogHeader from './CatalogHeader';
import SchedulingForm from './SchedulingForm';
import StepProcess from './StepProcess';

const Home = () => {
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [currency, setCurrency] = useState('MXN');
  const whatsappNumber = "+529844594738";
  
  const popularPlans = shopProducts.filter(p => 
    p.level === 'MÁS POPULAR' || p.level === 'RECOMENDADO' || p.level === 'LEGENDARIO'
  ).slice(0, 3);

  return (
    <>
      <Helmet>
        <title>OnlineClassesEnglish | La Universidad de Inglés 24/7 para México</title>
      </Helmet>

      <ProductModal product={selectedProduct} isOpen={!!selectedProduct} onClose={() => setSelectedProduct(null)} />

      <CatalogHeader />
      <Hero />

      <section className="py-10 bg-zinc-900 border-b border-green-900/30">
        <div className="max-w-7xl mx-auto px-4 flex flex-wrap justify-center gap-8 md:gap-16">
          <div className="flex items-center gap-3">
            <SafeIcon icon={FiLock} className="text-green-500 text-2xl" />
            <span className="text-white font-bold text-sm uppercase tracking-widest">Pago Seguro SSL</span>
          </div>
          <div className="flex items-center gap-3">
            <SafeIcon icon={FiCheckCircle} className="text-blue-500 text-2xl" />
            <span className="text-white font-bold text-sm uppercase tracking-widest">Resultados Garantizados</span>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex text-yellow-500">
              <SafeIcon icon={FiStar} className="fill-current" />
              <SafeIcon icon={FiStar} className="fill-current" />
              <SafeIcon icon={FiStar} className="fill-current" />
              <SafeIcon icon={FiStar} className="fill-current" />
              <SafeIcon icon={FiStar} className="fill-current" />
            </div>
            <span className="text-white font-bold text-sm uppercase tracking-widest">4.9/5 Student Rating</span>
          </div>
        </div>
      </section>

      <ProblemSolution />

      {/* --- ACTIVATION WORKFLOW SECTION (HOME) --- */}
      <section className="py-24 px-4 bg-black">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-7xl font-black text-white uppercase italic leading-none">
              Inicia tu <span className="text-yellow-500">Aventura</span>
            </h2>
            <p className="text-gray-400 mt-4 max-w-2xl mx-auto">Nuestro sistema automatizado te permite empezar a estudiar en minutos.</p>
          </div>
          <StepProcess />
          <SchedulingForm />
        </div>
      </section>

      <Features />
      <PricingComparison />
      <Specialties />

      {/* --- PRICING SECTION --- */}
      <section id="pricing" className="py-24 px-4 bg-black">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-flex bg-zinc-900 border border-zinc-700 rounded-full p-1 mb-6">
              <button onClick={() => setCurrency('MXN')} className={`px-6 py-2 rounded-full text-xs font-black transition-all ${currency === 'MXN' ? 'bg-yellow-500 text-black' : 'text-gray-500'}`}>MXN</button>
              <button onClick={() => setCurrency('USD')} className={`px-6 py-2 rounded-full text-xs font-black transition-all ${currency === 'USD' ? 'bg-yellow-500 text-black' : 'text-gray-500'}`}>USD</button>
            </div>
            <h2 className="text-4xl md:text-6xl font-black text-white mb-6 uppercase italic">Planes <span className="text-yellow-500">Flexibles</span></h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {popularPlans.map(plan => (
              <ProductCard key={plan.id} product={plan} onOpenDetails={setSelectedProduct} currency={currency} />
            ))}
          </div>
        </div>
      </section>

      <SocialLearning />
      <QuickFacts />
      <Biography />
      <MediaLogos />

      <section className="py-24 px-4 bg-gradient-to-br from-red-600 to-red-800 text-white text-center rounded-[3rem] mx-4 my-20 shadow-2xl relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-full bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10"></div>
        <div className="relative z-10 max-w-4xl mx-auto">
          <h2 className="text-4xl md:text-7xl font-black mb-6 uppercase leading-[0.9] italic tracking-tighter">
            ¿Listo para <br/> cambiar tu futuro?
          </h2>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="/checkout" className="bg-white text-red-600 px-12 py-6 rounded-[2rem] font-black text-xl uppercase tracking-widest hover:bg-zinc-100 transform hover:scale-105 transition-all shadow-2xl">
              Comenzar Ahora
            </a>
            <a href={`https://wa.me/${whatsappNumber}`} className="bg-black text-white px-12 py-6 rounded-[2rem] font-black text-xl uppercase tracking-widest hover:bg-zinc-900 flex items-center justify-center gap-3 transition-all border border-white/20">
              <SafeIcon icon={FaWhatsapp} /> Chat Soporte
            </a>
          </div>
        </div>
      </section>
    </>
  );
};

export default Home;